import { useEffect, useState, useMemo, useRef, useCallback } from 'react'

import { useRouter } from 'next/router'
import pusher from 'clients/pusher'
import PusherContext from 'contexts/pusher-context'
import useActiveWindow from 'hooks/use-active-window'
import usePrevious from 'hooks/use-previous'

const PusherProvider = ({ children }) => {
  const { query } = useRouter()
  const debug = !!query?.debugPusher
  const windowIsActive = useActiveWindow()

  const [isConnected, setIsConnected] = useState(true)
  const wasConnected = usePrevious(isConnected)
  const justReconnected = isConnected && wasConnected === false

  const channelSubscriptionCounts = useRef({})
  const channels = useRef({})

  useEffect(() => {
    if (!debug) return

    if (isConnected) {
      console.log('Pusher connected')
    } else {
      console.log('Pusher disconnected')
    }
  }, [debug, isConnected])

  const subscribe = useCallback(
    channelName => {
      const currentCount = channelSubscriptionCounts.current[channelName] || 0
      const newCount = currentCount + 1

      channelSubscriptionCounts.current[channelName] = newCount

      if (newCount === 1) {
        if (debug) {
          console.log('Subscribed: ', channelName)
        }
        channels.current[channelName] = pusher.subscribe(channelName)
      }

      return channels.current[channelName]
    },
    [debug]
  )

  const unsubscribe = useCallback(
    channelName => {
      const currentCount = channelSubscriptionCounts.current[channelName] || 0
      const newCount = Math.max(currentCount - 1, 0)

      channelSubscriptionCounts.current[channelName] = newCount

      if (newCount === 0) {
        if (debug) {
          console.log('Unsubscribed: ', channelName)
        }
        pusher.unsubscribe(channelName)
        delete channelSubscriptionCounts.current[channelName]
        delete channels.current[channelName]
      }
    },
    [debug]
  )

  useEffect(() => {
    if (!pusher) return

    if (windowIsActive) {
      if (!isConnected) pusher.connect()
    } else {
      if (isConnected) pusher.disconnect()
    }
  }, [windowIsActive, isConnected])

  useEffect(() => {
    const onConnected = () => setIsConnected(true)
    const onDisconnected = () => setIsConnected(false)
    const onUnload = () => pusher.disconnect()

    pusher.connect()
    pusher.connection.bind('connected', onConnected)
    pusher.connection.bind('disconnected', onDisconnected)
    window.addEventListener('beforeunload', onUnload)

    return () => {
      pusher.disconnect()
      pusher.connection.unbind('connected', onConnected)
      pusher.connection.unbind('disconnected', onDisconnected)
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [])

  const contextValue = useMemo(() => {
    return {
      pusher,
      justReconnected,
      subscribe,
      unsubscribe,
      debug,
    }
  }, [justReconnected, subscribe, unsubscribe, debug])

  return (
    <PusherContext.Provider value={contextValue}>
      {children}
    </PusherContext.Provider>
  )
}

export default PusherProvider
